/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import Image from '@/components/atoms/image/image';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import AnchorLink from '@/components/molecules/anchor-link/anchor-link';
import IconLabel from '@/components/molecules/icon-label/icon-label';
import StarRating from '@/components/molecules/star-rating/star-rating';
import { TagVariant } from '@/components/molecules/tag/tag';
import { SliderImageItem } from '@/components/organisms/image-slider/image-slider';
import TagList from '@/components/organisms/tag-list/tag-list';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import styles from './hotel-card.module.scss';

export interface HotelCardProps {
  /** The class name to apply */
  className?: string;
  /** The id to use for the component */
  id?: string;
  /** Image array to display in the slider */
  image?: SliderImageItem;
  /** Indicates whether the component is active */
  isActive?: boolean;
  /** Indicates whether to display the loading state */
  isLoading?: boolean;
  /** The name to display */
  name?: string;
  /** An event that is triggered on click */
  onClick?: () => void;
  /** Rating to display in the star rating*/
  rating?: number;
  /** Tag array to display within the card */
  tags?: {
    text: string;
    variant: TagVariant;
  }[];
  /** From price per person to show on the card */
  totalPricePerPerson?: number;
  /** Type of the property */
  type?: Content.Components['HotelType'];
  /** The url to link to */
  url?: string;
  /** Variant type to filter out items */
  variant?: 'compact';
}

const HotelCard = ({
  className,
  image,
  id,
  isActive = false,
  isLoading = false,
  name,
  onClick,
  rating = 0,
  tags = [],
  totalPricePerPerson,
  type,
  url,
  variant,
}: HotelCardProps) => {
  const { t } = useI18n();
  const currencyFormatter = useCurrencyFormatter();

  const types: Record<
    Exclude<Content.Components['HotelType'], 'Undefined'>,
    string
  > = {
    Apartment: t('items.common.words.apartment'),
    Chalet: t('items.common.words.chalet'),
    Hotel: t('items.common.words.hotel'),
    TransportHotel: t('items.common.words.transport'),
  };

  const iconName =
    type === 'TransportHotel'
      ? 'PlaneTakeOff'
      : type && type !== 'Undefined'
        ? type
        : undefined;

  const hasIcon = !!iconName;

  if (isLoading) {
    return (
      <div
        className={classNames(styles.component, styles.isLoading, className)}
      >
        <div className={styles.imageWrapper}>
          <SkeletonLoader height="100%" width="100%" />
        </div>

        <div className={styles.detailsWrapper}>
          <div className={styles.details}>
            <div className={styles.detailsHeader}>
              <SkeletonLoader borderRadius="4px" height="24px" width="100px" />

              <SkeletonLoader borderRadius="4px" height="24px" width="120px" />
            </div>

            <div className={styles.name}>
              <SkeletonLoader borderRadius="4px" height="24px" width="35%" />
            </div>

            <SkeletonLoader borderRadius="4px" height="16px" width="70%" />

            <SkeletonLoader borderRadius="4px" height="16px" width="50%" />

            <SkeletonLoader borderRadius="4px" height="16px" width="25%" />

            <div className={styles.fromPrice}>
              <SkeletonLoader borderRadius="4px" height="25px" width="40%" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      className={classNames(styles.component, className, {
        [styles.isActive]: isActive,
        [styles.isCompact]: variant === 'compact',
      })}
      id={id}
      onClick={onClick}
    >
      {image && (
        <div className={styles.imageWrapper}>
          <Image
            alt={image.altText}
            className={styles.image}
            src={image.url}
            srcSet={{
              desktop: { width: 236 },
              laptop: { width: 464 },
              tablet: { width: 612 },
            }}
            unoptimized
            width={343}
          />
        </div>
      )}

      <div className={styles.detailsWrapper}>
        <div className={styles.details}>
          <div className={styles.detailsHeader}>
            {hasIcon && type && (
              <IconLabel icon={iconName} text={types[type]} />
            )}

            <StarRating rating={rating} />
          </div>

          {name && (
            <Typography
              className={styles.name}
              tag="div"
              tagStyle="headingSmall"
            >
              {name}
            </Typography>
          )}

          {tags.length > 0 && (
            <TagList
              className={styles.tagList}
              hasSideScroll={variant === 'compact'}
              tags={tags}
            />
          )}

          {url && (
            <AnchorLink
              className={styles.link}
              icon="ArrowRight"
              text={t('items.hotelCard.viewHotelDetails')}
              url={url}
            />
          )}

          {totalPricePerPerson && (
            <div className={styles.fromPrice}>
              <Typography className={styles.name} tagStyle="priceSmall">
                {t('items.common.words.from')}
              </Typography>

              <Typography className={styles.name} tagStyle="priceMedium">
                {currencyFormatter(totalPricePerPerson)}
              </Typography>
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

HotelCard.displayName = 'HotelCard';

export default HotelCard;
